import { z } from 'zod'

import {
  MarketingCampaignStatus,
  MarketingChannel,
} from '../constants/marketing'
import { dbEntitySchema } from './system'
import { marketingEntityScopeSchema } from './marketingScope'

export const marketingCampaignSchema = dbEntitySchema
  .and(marketingEntityScopeSchema)
  .and(
    z.object({
      name: z.string().nullish(),
      channels: z.array(z.nativeEnum(MarketingChannel)).min(1),
      marketingList: z.string().nullish(),
      marketingSegment: z.string().nullish(),
      marketingTemplate: z.string().nullish(),
      emailSender: z.string().nullish(),
      subject: z.string().nullish(),
      content: z.string().nullish(),
      fromName: z.string().nullish(),
      replyTo: z.string().nullish(),
      previewText: z.string().nullish(),
      utmParams: z.record(z.string()).nullish(),
      status: z.nativeEnum(MarketingCampaignStatus),
      scheduledAt: z.date().nullish(),
      sentAt: z.date().nullish(),
    })
  )

export const marketingCampaignCreateDraftArgsSchema =
  marketingEntityScopeSchema.and(
    z
      .object({
        name: z.string().min(1).optional(),
        channels: z
          .array(z.nativeEnum(MarketingChannel))
          .min(1)
          .default([MarketingChannel.EMAIL]),
        marketingList: z.string().nullish(),
        marketingSegment: z.string().nullish(),
        marketingTemplate: z.string().nullish().optional(),
        emailSender: z.string().nullish().optional(),
        subject: z.string().min(1).nullish().optional(),
        content: z.string(),
        fromName: z.string().nullish().optional(),
        replyTo: z.string().email().nullish().optional(),
        previewText: z.string().nullish().optional(),
        utmParams: z.record(z.string()).nullish().optional(),
      })
      .superRefine((data, ctx) => {
        const channels = Array.from(new Set(data.channels))
        if (channels.length !== data.channels.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'channels must be unique',
            path: ['channels'],
          })
        }
        if (!data.marketingTemplate?.trim() && !data.content.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'content is required when no template is linked',
            path: ['content'],
          })
        }
        if (channels.includes(MarketingChannel.EMAIL)) {
          if (!data.subject?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'subject is required when email channel is selected',
              path: ['subject'],
            })
          }
          return
        }
        if (data.fromName || data.replyTo || data.previewText) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'fromName/replyTo/previewText require email channel selection',
            path: ['channels'],
          })
        }
      })
  )

export const marketingCampaignUpdateDraftArgsSchema = z
  .object({
    name: z.string().min(1).nullish().optional(),
    channels: z.array(z.nativeEnum(MarketingChannel)).min(1).optional(),
    marketingList: z.string().nullish().optional(),
    marketingSegment: z.string().nullish().optional(),
    marketingTemplate: z.string().nullish().optional(),
    emailSender: z.string().nullish().optional(),
    subject: z.string().min(1).nullish().optional(),
    content: z.string().min(1).optional(),
    fromName: z.string().nullish().optional(),
    replyTo: z.string().email().nullish().optional(),
    previewText: z.string().nullish().optional(),
    utmParams: z.record(z.string()).nullish().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.channels) return
    const channels = Array.from(new Set(data.channels))
    if (channels.length !== data.channels.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'channels must be unique',
        path: ['channels'],
      })
    }
    if (channels.includes(MarketingChannel.EMAIL)) return
    if (data.fromName || data.replyTo || data.previewText) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'fromName/replyTo/previewText require email channel selection',
        path: ['channels'],
      })
    }
  })
